/**
 * Pricing page data constants — plan features, FAQ entries, add-on definitions,
 * and testimonial data.
 */

// PLAN FEATURES

export const basicFeatures = [
  'Text/SMS & MMS messaging',
  'Smart-tracking E&O guardrails',
  'System Integration',
  'Automated Logging',
  'Unlimited Team Messaging',
  'Contact Management',
  'File Sharing',
  'Unlimited message history',
  'Security and Compliance',
  'Bulk messaging',
  'Host & Porting Assistance',
  'Federal Compliance',
];

// CREDIT CALCULATOR

export const BASE_CREDITS = 1000;
export const ADDITIONAL_CREDITS_PER_SEAT = 200;

export function calculateTotalCredits(userCount: number): number {
  return BASE_CREDITS + Math.max(0, userCount - 1) * ADDITIONAL_CREDITS_PER_SEAT;
}

// ADD-ON DEFINITIONS

export type AddOnType = 'VOIP Calling' | 'Mobile App' | 'RCS Messaging';

export interface AddOnFeature {
  text: string;
}

export interface AddOnDefinition {
  name: AddOnType;
  description: string;
  price: string;
  priceLabel: string;
  pricingNote: string;
  features: AddOnFeature[];
}

export const addOns: AddOnDefinition[] = [
  {
    name: 'VOIP Calling',
    description:
      'Add cloud calling to Voley so your team can handle calls and texts from the same shared number, with automatic call logging to your AMS.',
    price: '$25',
    priceLabel: 'per user / month',
    pricingNote:
      'This add-on increases your Core plan by $25 per user each month and adds $85/month to the Core base price.',
    features: [
      { text: 'Shared agency numbers with intelligent routing and voicemail handling.' },
      { text: "Call history stored alongside each client\u2019s text conversations." },
      { text: 'Built to layer onto your existing phone setup without a full replacement.' },
    ],
  },
  {
    name: 'Mobile App',
    description:
      'Keep your team connected to Voley on the go with a dedicated iOS and Android app for calls, texts, and team chat.',
    price: '$5',
    priceLabel: 'per user / month',
    pricingNote:
      'This add-on increases your Core plan by $5 per user each month and adds $20/month to the Core base price.',
    features: [
      { text: 'Full access to conversations, contacts, and notifications from iOS and Android.' },
      { text: 'Push alerts so producers and account managers never miss an important message.' },
      { text: 'Built for shared inbox workflows, not 1:1 texting from a personal phone.' },
    ],
  },
  {
    name: 'RCS Messaging',
    description:
      "Upgrade Android conversations from plain SMS to branded RCS with your agency name, logo, and verified badge so clients instantly know it\u2019s you.",
    price: '$10',
    priceLabel: 'per user / month',
    pricingNote:
      'This add-on increases your Core plan by $10 per user each month and adds $35/month to the Core base price.',
    features: [
      { text: 'Branded, verified messages that stand out from anonymous SMS.' },
      { text: 'Rich media support for quotes, reminders, and policy updates.' },
      { text: 'Works alongside your existing texting without changing your number.' },
    ],
  },
];

// FAQ DATA

export interface FAQEntry {
  question: string;
  answer: string;
}

export const faqsLeft: FAQEntry[] = [
  {
    question: 'How is the client count determined?',
    answer:
      'A client is counted as a unique contact that is synced from your AMS or created in our system. The pricing is based on your total number of contacts, so you have full flexibility to communicate with all your clients.',
  },
  {
    question: 'Do you offer annual billing?',
    answer:
      "Yes, you can save 15% by paying annually instead of monthly. This discount is automatically applied when you select the annual billing option. You'll be billed once per year at the discounted rate.",
  },
  {
    question: 'How long does it take to get set up?',
    answer:
      'Most agencies are up and running within 1-2 days. Our onboarding specialists will guide you through the process, help configure your integrations, and train your team to ensure a smooth transition.',
  },
  {
    question: 'Are contracts required?',
    answer:
      'We offer both options. Monthly billing is a month-to-month service with no long-term commitments. Annual billing requires a one-year commitment but comes with a 15% discount. Both options provide full access to all features.',
  },
];

export const faqsRight: FAQEntry[] = [
  {
    question: 'What integrations do you support?',
    answer:
      'We integrate with popular insurance AMS systems including HawkSoft, Applied Epic, AgencyZoom, and more. The standard plan includes basic integration capabilities to sync contacts and ensure consistent communication records.',
  },
  {
    question: 'Is there a limit on team members?',
    answer:
      'No, all plans include unlimited team members. You only pay based on your client count, not the size of your team. This means your whole agency can use Voley without any additional per-user fees.',
  },
  {
    question: 'What kind of support is included?',
    answer:
      'All customers receive access to our support team via chat, email, and phone during business hours. We also provide comprehensive documentation, training materials, and regular webinars to help you get the most out of Voley.',
  },
  {
    question: 'Is my data secure with Voley?',
    answer:
      'Absolutely. We use industry-standard encryption and secure cloud infrastructure to protect all your data. All communications are encrypted in transit and at rest, and we maintain strict access controls and regular security audits.',
  },
];

// TESTIMONIALS

export interface Testimonial {
  quote: string;
  name: string;
  title: string;
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'Voley has centralized all our communications into one place, which has made our team more efficient and our clients happier. The pricing is straightforward and the ROI has been incredible.',
    name: 'Brent Thurman',
    title: 'Keystone Insurance, Agency Owner',
  },
  {
    quote:
      "The simplicity of Voley\u2019s platform has transformed how we interact with clients. Our team picked it up immediately, and the value we\u2019ve seen in terms of organization is worth every penny.",
    name: 'Jerris Koplin',
    title: 'Do Right Insurance, Agency Owner',
  },
  {
    quote:
      'Having a single place to manage all client conversations has been a game-changer for our agency. The integration with our AMS makes Voley an essential part of our tech stack.',
    name: 'Sarah Beasley',
    title: 'Liberty Shield Insurance, Operations Manager',
  },
];
